import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormProvider } from './FormContext';

/**
 * A standard Form in the Kernel UI
 *
 * This Component is responsible for managing the state of the form (values, errorState, and mode)
 * The state is provided to child FormField components via FormContext
 *
 * Form Fields are expected to interact with the FormContext to assign values and errors, and they
 * are also responsible for their own display logic
 *
 * Optionally, the Form can receive its values from a parent via formValues and setFormValues props.
 * If these are not provided, the Form will manage its own internal state
 */
export const Form = ({
  children,
  formValues: formValuesProp,
  setFormValues: setFormValuesProp,
  ref,
  variant = 'filled',
}) => {
  // State
  const [internalFormValues, setInternalFormValues] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [touchedStates, setTouchedStates] = useState({});

  const formValues = formValuesProp ?? internalFormValues;
  const setFormValues = setFormValuesProp ?? setInternalFormValues;

  // API exposed to the Parent
  useImperativeHandle(ref, () => {
    return {
      formValues,
      setFormValues,
      assignFormValues: (newValues) => {
        setFormValues((prevValues) => ({ ...prevValues, ...newValues }));
      },
      hasErrorState: () => {
        return Object.values(errorStates).some((value) => value !== null && value !== undefined);
      },
      touchAll: () => {
        setTouchedStates((prev) => Object.fromEntries(Object.keys(prev).map((key) => [key, true])));
      },
    };
  }, [formValues, errorStates, setFormValues]);

  // Cleanup Queue - Used by FormFields to cleanup if they unmount.
  // This pattern is necessary to prevent the formValues from being cleared if this Form is in controlled
  // mode and the entire Form is unmounting. This works by processing the cleanupQueue on the subsequent render cycle.
  // If the entire Form unmounts, the queue will be gone before the next render cycle can happen
  const cleanupQueueRef = useRef([]);
  const [cleanupTick, setCleanupTick] = useState(0);

  const enqueueCleanup = (entry) => {
    cleanupQueueRef.current.push(entry);
    setCleanupTick((tick) => tick + 1);
  };

  useEffect(() => {
    if (cleanupQueueRef.current.length === 0) return;

    const entries = cleanupQueueRef.current;
    cleanupQueueRef.current = [];

    setFormValues((prevValues) => {
      const nextValues = { ...prevValues };
      entries.forEach(({ valueKeys }) => {
        valueKeys.forEach((valueKey) => {
          nextValues[valueKey] = null;
        });
      });
      return nextValues;
    });

    setErrorStates((prev) => {
      const next = { ...prev };
      entries.forEach(({ field }) => {
        next[field] = null;
      });
      return next;
    });

    setTouchedStates((prev) => {
      const next = { ...prev };
      entries.forEach(({ field }) => {
        next[field] = null;
      });
      return next;
    });
  }, [cleanupTick]);

  return (
    <FormProvider
      formValues={formValues}
      setFormValues={setFormValues}
      errorStates={errorStates}
      setErrorStates={setErrorStates}
      touchedStates={touchedStates}
      setTouchedStates={setTouchedStates}
      enqueueCleanup={enqueueCleanup}
      variant={variant}
    >
      {children}
    </FormProvider>
  );
};
